import { useState, type ReactNode } from 'react';
import { useUiStore } from '../store/ui';
import { copyToClipboard } from '../lib/clipboard';

/* ---------------- Button ---------------- */

export type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost';
export interface ButtonProps {
  variant?: ButtonVariant;
  size?: 'sm' | 'md';
  disabled?: boolean;
  onClick?: () => void;
  type?: 'button' | 'submit';
  children: ReactNode;
  className?: string;
  title?: string;
}
export function Button(props: ButtonProps) {
  const { variant = 'primary', size = 'md', ...rest } = props;
  return (
    <button
      type={props.type ?? 'button'}
      className={`btn ${variant} ${size === 'sm' ? 'sm' : ''} ${props.className ?? ''}`.trim()}
      disabled={props.disabled}
      onClick={props.onClick}
      title={props.title}
    >
      {rest.children}
    </button>
  );
}

/* ---------------- Inputs ---------------- */

export interface InputProps {
  label?: string;
  type?: string;
  value: string;
  placeholder?: string;
  onChange: (value: string) => void;
  required?: boolean;
  autoComplete?: string;
  hint?: string;
  rows?: number;
  disabled?: boolean;
}
export function Input({ label, ...p }: InputProps) {
  return (
    <div className="field">
      {label ? <label htmlFor={label}>{label}</label> : null}
      <input
        id={label}
        className="input"
        type={p.type ?? 'text'}
        value={p.value}
        placeholder={p.placeholder}
        onChange={(e) => p.onChange(e.target.value)}
        autoComplete={p.autoComplete}
        required={p.required}
        disabled={p.disabled}
      />
      {p.hint ? <div className="text-muted text-xs">{p.hint}</div> : null}
    </div>
  );
}

export function Textarea(props: InputProps) {
  return (
    <div className="field">
      {props.label ? <label htmlFor={props.label}>{props.label}</label> : null}
      <textarea
        id={props.label}
        className="input"
        value={props.value}
        placeholder={props.placeholder}
        onChange={(e) => props.onChange(e.target.value)}
        rows={props.rows ?? 3}
        disabled={props.disabled}
      />
    </div>
  );
}

export interface SelectProps {
  label?: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
  disabled?: boolean;
  hint?: string;
}
export function Select(props: SelectProps) {
  return (
    <div className="field">
      {props.label ? <label htmlFor={props.label}>{props.label}</label> : null}
      <select
        id={props.label}
        className="input"
        value={props.value}
        disabled={props.disabled}
        onChange={(e) => props.onChange(e.target.value)}
      >
        {props.options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      {props.hint ? <div className="text-muted text-xs">{props.hint}</div> : null}
    </div>
  );
}

/* ---------------- Card / Badge / Metric ---------------- */

export function Card(props: { title?: string; children: ReactNode; action?: ReactNode; className?: string }) {
  return (
    <div className={`card ${props.className ?? ''}`.trim()}>
      {props.title ? (
        <div className="flex justify-between items-center mb-2">
          <h3 className="card-title">{props.title}</h3>
          {props.action}
        </div>
      ) : null}
      {props.children}
    </div>
  );
}

const STATUS_CLASS: Record<string, string> = {
  LIVE: 'live', CONNECTING: 'connecting', RECONNECTING: 'reconnecting',
  OFFLINE: 'offline', ERROR: 'error', FAILED: 'failed', DISABLED: 'disabled',
  CONNECTED: 'connected', ACTIVE: 'active', SUSPENDED: 'suspended',
  PAST_DUE: 'past_due', CANCELLED: 'cancelled', EXPIRED: 'expired', PENDING: 'pending',
  PAID: 'paid', REFUNDED: 'refunded', WARNING: 'warning', ARCHIVED: 'archived',
};

export function StatusBadge(props: { status: string; label?: string }) {
  const key = props.status.toUpperCase();
  const cls = STATUS_CLASS[key] ?? 'offline';
  return (
    <span className={`badge status-${cls}`}>
      <span className="dot" />
      {props.label ?? props.status}
    </span>
  );
}

export function Badge({ children, tone = 'info' }: { children: ReactNode; tone?: string }) {
  return <span className={`badge status-${tone}`}>{children}</span>;
}

/* ---------------- Avatar ---------------- */

/** Circular initials chip used in chat message rows. */
export function Avatar(props: { name: string; size?: 'sm' | 'md'; className?: string }) {
  const initials = props.name.trim().split(/\s+/).map((w) => w[0]).slice(0, 2).join('').toUpperCase() || '?';
  return (
    <span
      className={`avatar ${props.className ?? ''}`.trim()}
      title={props.name}
      aria-hidden
      style={props.size === 'sm' ? { width: '1.75rem', height: '1.75rem', fontSize: '0.62rem' } : undefined}
    >
      {initials}
    </span>
  );
}

export function MetricCard(props: {
  label: string;
  value: string | number;
  hint?: ReactNode;
  /** Optional tonal icon chip rendered in the card's top-right corner. */
  icon?: ReactNode;
  tone?: 'primary' | 'success' | 'info' | 'warning' | 'danger';
}) {
  return (
    <div className="metric-card">
      {props.icon ? (
        <span className={`metric-icon tone-${props.tone ?? 'primary'}`} aria-hidden>
          {props.icon}
        </span>
      ) : null}
      <div className="metric-label">{props.label}</div>
      <div className="metric-value">{props.value}</div>
      {props.hint ? <div className="metric-hint text-muted text-xs">{props.hint}</div> : null}
    </div>
  );
}

/* ---------------- Alert / Spinner / EmptyState ---------------- */

export function Alert(props: { kind: 'info' | 'success' | 'warning' | 'danger'; children: ReactNode }) {
  return <div className={`alert ${props.kind}`}>{props.children}</div>;
}

export function CopyButton({ text, label }: { text: string; label?: string }) {
  const ui = useUiStore();
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (!text) {
      ui.toast('danger', 'Nothing to copy');
      return;
    }
    const ok = await copyToClipboard(text);
    if (!ok) {
      ui.toast('danger', 'Failed to copy');
      return;
    }
    setCopied(true);
    ui.toast('success', 'Copied to clipboard');
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button type="button" className="copy-btn" onClick={() => void handleCopy()} title="Copy to clipboard">
      {copied ? 'Copied!' : (label ?? 'Copy')}
    </button>
  );
}

/* Re-export companion components from feedback for convenience */
export { Modal, ConfirmDialog, DataTable, Pagination, Tabs, Toasts, SpinnerBlock, useNow, formatDuration, formatKbps } from './feedback';

export function EmptyState(props: { title: string; body?: string; action?: ReactNode }) {
  return (
    <div className="empty-state">
      <h4>{props.title}</h4>
      {props.body ? <p>{props.body}</p> : null}
      {props.action ? <div className="mt-2">{props.action}</div> : null}
    </div>
  );
}